import PropTypes from 'prop-types';
import classNames from 'classnames';
import { makeStyles } from '@material-ui/core';
import UserAvatar from '../../../userAvatar';
import HorizontalAnswersGroup from './horizontalAnswersGroup';
import ToggleComponent from '../../../toggleComponent';
import ActionButton from '../../../actionButton';
import { getPersonShortName } from '../../../../../utility/helpers';
import { ACTION_BUTTON_TYPES } from '../../../actionButton/config';

const useStyles = makeStyles(
  ({ breakpoints, palette: { primary }, spacing }) => ({
    main: {
      display: 'flex',
      flexDirection: 'column',
    },
    reviewAnswers: {
      display: 'flex',
      width: '100%',
    },
    groupReviewAnswers: {
      height: '100%',
      [breakpoints.up('sm')]: {
        height: 'auto',
        maxHeight: '100%',
      },
    },
    groupReviewAnswer: {
      boxSizing: 'border-box',
      border: `1px solid ${primary.bluish6}`,
      borderRadius: 8,
      display: 'flex',
      flexDirection: 'column',
      marginBottom: spacing(2),
      userSelect: 'none',
      '&:last-of-type': {
        marginBottom: 0,
      },
    },
    subjectHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    addCommentButton: {
      width: 32,
      height: 32,
      marginRight: spacing(2),
    },
    commentIconDisabled: {
      '& path': {
        fill: primary.bluish7,
      },
    },
    addIcon: {
      width: 20,
      height: 20,
    },
    editIcon: {
      width: 20,
      height: 18,
    },
    subject: {
      margin: spacing(2, 2, 0, 2),
      [breakpoints.up('sm')]: {
        margin: spacing(4, 4, 0, 4),
      },
    },
    userAvatar: {
      height: 20,
      width: 20,
      fontSize: 7,
      lineHeight: '18px',
      [breakpoints.up('sm')]: {
        fontSize: 14,
        lineHeight: 1,
        height: 32,
        width: 32,
      },
    },
    userLabel: {
      fontFamily: 'ProximaNova-Bold',
      fontSize: 12,
      lineHeight: '14px',
      whiteSpace: 'nowrap',
      [breakpoints.up('sm')]: {
        fontSize: 16,
        lineHeight: '18px',
      },
    },
    answersScroll: {
      display: 'flex',
    },
    horizontalWeightLabel: {
      fontSize: 12,
      fontFamily: 'ProximaNova-Bold',
      marginLeft: spacing(2),
      marginTop: spacing(23),
      marginRight: spacing(2),
    },
  })
);

const HorizontalAnswers = ({
  neutralAnswerLabel,
  weightLabel = '',
  subjects = [],
  answers,
  selection = {},
  questionId = 0,
  isSingleReview = false,
  isDisabled = false,
  isInsightsView = false,
  isAttributePreview = false,
  hasAddCommentButton = false,
  onSelect = () => {},
  onAddComment = () => {},
}) => {
  const classes = useStyles();

  const [subject] = subjects;

  return (
    <div className={classes.main}>
      {isSingleReview ? (
        <div className={classes.reviewAnswers}>
          <HorizontalAnswersGroup
            questionId={questionId}
            subject={subject}
            neutralAnswerLabel={neutralAnswerLabel}
            answers={answers}
            selection={selection}
            isSingleReview={isSingleReview}
            isDisabled={isDisabled}
            isAttributePreview={isAttributePreview}
            hasAddCommentButton={hasAddCommentButton}
            onSelect={onSelect}
          />
          {isAttributePreview && (
            <div className={classes.horizontalWeightLabel}>{weightLabel}</div>
          )}
        </div>
      ) : (
        <div className={classes.groupReviewAnswers}>
          {subjects.map(currentSubject => {
            const isEditComment =
              !!currentSubject?.replied?.[questionId]?.reply;
            const isSelected =
              !!selection?.[currentSubject?.review_tracking]?.[questionId];
            return (
              <div
                key={`review_subject_${currentSubject.id}`}
                className={classes.groupReviewAnswer}
              >
                <div className={classes.subjectHeader}>
                  <UserAvatar
                    className={classes.subject}
                    customAvatarClass={classes.userAvatar}
                    labelClass={classes.userLabel}
                    getCustomCaption={getPersonShortName}
                    user={currentSubject}
                    caption
                  />
                  {hasAddCommentButton &&
                    !(isInsightsView && !isEditComment) && (
                      <ToggleComponent inKey={isEditComment}>
                        {isEditComment ? (
                          <ActionButton
                            iconButtonClass={classes.addCommentButton}
                            iconClass={classNames(classes.editIcon, {
                              [classes.commentIconDisabled]:
                                !isInsightsView && (isDisabled || !isSelected),
                            })}
                            type={ACTION_BUTTON_TYPES.EDIT_COMMENT}
                            isDisabled={
                              !isInsightsView && (isDisabled || !isSelected)
                            }
                            onClickHandler={() =>
                              onAddComment(currentSubject, questionId)
                            }
                          />
                        ) : (
                          <ActionButton
                            iconButtonClass={classes.addCommentButton}
                            iconClass={classNames(classes.addIcon, {
                              [classes.commentIconDisabled]:
                                isDisabled || !isSelected,
                            })}
                            type={ACTION_BUTTON_TYPES.ADD_COMMENT}
                            isDisabled={isDisabled || !isSelected}
                            onClickHandler={() => onAddComment(currentSubject)}
                          />
                        )}
                      </ToggleComponent>
                    )}
                </div>
                <div
                  className={classNames({
                    [classes.reviewAnswers]: isAttributePreview,
                  })}
                >
                  <HorizontalAnswersGroup
                    questionId={questionId}
                    subject={currentSubject}
                    isSingleReview={isSingleReview}
                    isAttributePreview={isAttributePreview}
                    isDisabled={isDisabled}
                    neutralAnswerLabel={neutralAnswerLabel}
                    answers={answers}
                    selection={selection}
                    hasAddCommentButton={hasAddCommentButton}
                    onSelect={onSelect}
                  />
                  {isAttributePreview && (
                    <div className={classes.horizontalWeightLabel}>
                      {weightLabel}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

HorizontalAnswers.propTypes = {
  weightLabel: PropTypes.string,
  answers: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  questionId: PropTypes.number,
  subjects: PropTypes.arrayOf(PropTypes.shape({})),
  isSingleReview: PropTypes.bool,
  isInsightsView: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isAttributePreview: PropTypes.bool,
  hasAddCommentButton: PropTypes.bool,
  neutralAnswerLabel: PropTypes.string.isRequired,
  selection: PropTypes.shape({}),
  onSelect: PropTypes.func,
  onAddComment: PropTypes.func,
};

export default HorizontalAnswers;
